import { MOCK_PROFILES, MOCK_SECTIONS } from './constants';

const LS_KEY = 'wes_hub360_mock_state_v1';

const uid = () => Math.random().toString(16).slice(2) + Date.now().toString(16);

const defaultState = () => {
  const allSectionKeys = MOCK_SECTIONS.map((s) => s.key);

  const platformTenant = {
    id: 'platform',
    name: 'WES Hub 360',
    createdAt: new Date().toISOString(),
    allowedSectionsForAdmCliente: allSectionKeys,
    sectors: [],
    users: [],
    isPlatform: true,
  };

  const tenantA = {
    id: 'tenant_a',
    name: 'Empresa Alpha',
    createdAt: new Date().toISOString(),
    // ADM WES escolhe o que o ADM Cliente pode acessar
    allowedSectionsForAdmCliente: allSectionKeys,
    // Setores do tenant (gerenciado pelo ADM Cliente)
    sectors: [
      { id: 'sec_1', name: 'Financeiro', createdAt: new Date().toISOString() },
      { id: 'sec_2', name: 'Operações', createdAt: new Date().toISOString() },
    ],
    // Usuários (mock)
    users: [
      {
        id: 'u_admin_1',
        email: 'adm.cliente@alpha.com',
        full_name: 'Admin Cliente Alpha',
        type: MOCK_PROFILES.ADM_CLIENTE,
        createdAt: new Date().toISOString(),
      },
      {
        id: 'u_user_1',
        email: 'user@alpha.com',
        full_name: 'Usuário Comum Alpha',
        type: MOCK_PROFILES.USER_CLIENTE,
        createdAt: new Date().toISOString(),
        // ADM Cliente escolhe o que o usuário comum pode acessar
        allowedSectionsForUser: ['dashboard', 'automation', 'processes'],
      },
    ],
  };

  const tenantB = {
    id: 'tenant_b',
    name: 'Empresa Beta',
    createdAt: new Date().toISOString(),
    allowedSectionsForAdmCliente: ['dashboard', 'automation', 'integrations', 'processes'],
    sectors: [{ id: 'sec_3', name: 'TI', createdAt: new Date().toISOString() }],
    users: [
      {
        id: 'u_admin_2',
        email: 'adm.cliente@beta.com',
        full_name: 'Admin Cliente Beta',
        type: MOCK_PROFILES.ADM_CLIENTE,
        createdAt: new Date().toISOString(),
      },
      {
        id: 'u_user_2',
        email: 'user@beta.com',
        full_name: 'Usuário Comum Beta',
        type: MOCK_PROFILES.USER_CLIENTE,
        createdAt: new Date().toISOString(),
        allowedSectionsForUser: ['dashboard'],
      },
    ],
  };

  return {
    session: {
      profile: MOCK_PROFILES.ADM_WES,
      email: 'admin@1wes.com',
      tenantId: platformTenant.id,
      userId: 'mock_user',
    },
    tenants: [platformTenant, tenantA, tenantB],
  };
};

export const loadMockState = () => {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return defaultState();
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return defaultState();

    // Migration: ensure platform tenant exists (older localStorage may not have it).
    const tenants = Array.isArray(parsed.tenants) ? parsed.tenants : [];
    const hasPlatform = tenants.some((t) => t && t.id === 'platform');
    if (!hasPlatform) {
      const fresh = defaultState();
      parsed.tenants = [fresh.tenants[0], ...tenants];
    }
    // If session is ADM WES, default to platform tenant when missing/invalid.
    if (parsed.session?.profile === MOCK_PROFILES.ADM_WES) {
      if (!parsed.session.email || parsed.session.email === 'viniciusaiires@hotmail.com') {
        parsed.session.email = 'admin@1wes.com';
      }
      const tid = parsed.session?.tenantId;
      const valid = (parsed.tenants || []).some((t) => t && t.id === tid);
      if (!valid) {
        parsed.session.tenantId = 'platform';
      }
    }
    return parsed;
  } catch (e) {
    return defaultState();
  }
};

export const saveMockState = (state) => {
  localStorage.setItem(LS_KEY, JSON.stringify(state));
};

export const resetMockState = () => {
  localStorage.removeItem(LS_KEY);
  const state = defaultState();
  saveMockState(state);
  return state;
};

export const withState = (fn) => {
  const state = loadMockState();
  const next = fn(structuredClone ? structuredClone(state) : JSON.parse(JSON.stringify(state)));
  saveMockState(next);
  return next;
};

export const createTenant = ({ name }) =>
  withState((state) => {
    const trimmed = String(name || '').trim();
    if (!trimmed) throw new Error('Nome da empresa é obrigatório');
    const exists = state.tenants.some((t) => t.name.toLowerCase() === trimmed.toLowerCase());
    if (exists) throw new Error('Já existe uma empresa com esse nome');
    state.tenants.unshift({
      id: `tenant_${uid()}`,
      name: trimmed,
      createdAt: new Date().toISOString(),
      allowedSectionsForAdmCliente: MOCK_SECTIONS.map((s) => s.key),
      sectors: [],
      users: [],
    });
    return state;
  });

export const updateTenantAllowedSectionsForAdmCliente = ({ tenantId, allowedSectionKeys }) =>
  withState((state) => {
    const tenant = state.tenants.find((t) => t.id === tenantId);
    if (!tenant) throw new Error('Empresa não encontrada');
    tenant.allowedSectionsForAdmCliente = Array.from(new Set(allowedSectionKeys || []));
    return state;
  });

export const addSector = ({ tenantId, name }) =>
  withState((state) => {
    const tenant = state.tenants.find((t) => t.id === tenantId);
    if (!tenant) throw new Error('Empresa não encontrada');
    const trimmed = String(name || '').trim();
    if (!trimmed) throw new Error('Nome do setor é obrigatório');
    const exists = tenant.sectors.some((s) => s.name.toLowerCase() === trimmed.toLowerCase());
    if (exists) throw new Error('Já existe um setor com esse nome');
    tenant.sectors.unshift({ id: `sec_${uid()}`, name: trimmed, createdAt: new Date().toISOString() });
    return state;
  });

export const renameSector = ({ tenantId, sectorId, name }) =>
  withState((state) => {
    const tenant = state.tenants.find((t) => t.id === tenantId);
    if (!tenant) throw new Error('Empresa não encontrada');
    const sector = tenant.sectors.find((s) => s.id === sectorId);
    if (!sector) throw new Error('Setor não encontrado');
    const trimmed = String(name || '').trim();
    if (!trimmed) throw new Error('Nome do setor é obrigatório');
    sector.name = trimmed;
    return state;
  });

export const deleteSector = ({ tenantId, sectorId }) =>
  withState((state) => {
    const tenant = state.tenants.find((t) => t.id === tenantId);
    if (!tenant) throw new Error('Empresa não encontrada');
    tenant.sectors = tenant.sectors.filter((s) => s.id !== sectorId);
    return state;
  });

export const updateUserAllowedSections = ({ tenantId, userId, allowedSectionKeys }) =>
  withState((state) => {
    const tenant = state.tenants.find((t) => t.id === tenantId);
    if (!tenant) throw new Error('Empresa não encontrada');
    const user = tenant.users.find((u) => u.id === userId);
    if (!user) throw new Error('Usuário não encontrado');
    user.allowedSectionsForUser = Array.from(new Set(allowedSectionKeys || []));
    return state;
  });

export const createAdmCliente = ({ tenantId, email, full_name }) =>
  withState((state) => {
    const tenant = state.tenants.find((t) => t.id === tenantId);
    if (!tenant) throw new Error('Empresa não encontrada');
    const normalizedEmail = String(email || '').trim().toLowerCase();
    if (!normalizedEmail) throw new Error('Email é obrigatório');
    if (!normalizedEmail.includes('@')) throw new Error('Email inválido');
    const name = String(full_name || '').trim();
    if (!name) throw new Error('Nome é obrigatório');
    const exists = (tenant.users || []).some((u) => String(u.email || '').toLowerCase() === normalizedEmail);
    if (exists) throw new Error('Já existe um usuário com esse email nesta empresa');
    tenant.users = [
      {
        id: `u_${uid()}`,
        email: normalizedEmail,
        full_name: name,
        type: MOCK_PROFILES.ADM_CLIENTE,
        password: null,
        createdAt: new Date().toISOString(),
      },
      ...(tenant.users || []),
    ];
    return state;
  });

export const createCompanyWithAdmCliente = ({ company_name, adm_email, adm_full_name, adm_password }) =>
  withState((state) => {
    const companyName = String(company_name || '').trim();
    if (!companyName) throw new Error('Nome da empresa é obrigatório');
    const exists = state.tenants.some((t) => t.name.toLowerCase() === companyName.toLowerCase());
    if (exists) throw new Error('Já existe uma empresa com esse nome');

    const normalizedEmail = String(adm_email || '').trim().toLowerCase();
    if (!normalizedEmail) throw new Error('Email do ADM Cliente é obrigatório');
    if (!normalizedEmail.includes('@')) throw new Error('Email do ADM Cliente inválido');
    const fullName = String(adm_full_name || '').trim();
    if (!fullName) throw new Error('Nome do ADM Cliente é obrigatório');
    const password = String(adm_password || '');
    if (!password) throw new Error('Senha do ADM Cliente é obrigatória');
    if (password.length < 8) throw new Error('Senha deve ter pelo menos 8 caracteres');

    const tenantId = `tenant_${uid()}`;
    const newTenant = {
      id: tenantId,
      name: companyName,
      createdAt: new Date().toISOString(),
      allowedSectionsForAdmCliente: MOCK_SECTIONS.map((s) => s.key),
      sectors: [],
      users: [
        {
          id: `u_${uid()}`,
          email: normalizedEmail,
          full_name: fullName,
          type: MOCK_PROFILES.ADM_CLIENTE,
          // mock only: do not use in production
          password,
          createdAt: new Date().toISOString(),
        },
      ],
    };

    state.tenants.unshift(newTenant);
    return state;
  });

export const deleteTenant = ({ tenantId }) =>
  withState((state) => {
    if (!tenantId) throw new Error('tenantId é obrigatório');
    if (tenantId === 'platform') throw new Error('Não é possível excluir o WES Hub 360');
    const exists = (state.tenants || []).some((t) => t.id === tenantId);
    if (!exists) throw new Error('Empresa não encontrada');

    state.tenants = (state.tenants || []).filter((t) => t.id !== tenantId);

    // If the current session tenant was deleted, fall back to platform.
    if (state.session?.tenantId === tenantId) {
      state.session.tenantId = 'platform';
    }
    return state;
  });

export const createCommonUser = ({ tenantId, email, full_name, password }) =>
  withState((state) => {
    const tenant = state.tenants.find((t) => t.id === tenantId);
    if (!tenant) throw new Error('Empresa não encontrada');
    const normalizedEmail = String(email || '').trim().toLowerCase();
    if (!normalizedEmail) throw new Error('Email é obrigatório');
    if (!normalizedEmail.includes('@')) throw new Error('Email inválido');
    const name = String(full_name || '').trim();
    if (!name) throw new Error('Nome é obrigatório');
    const pwd = String(password || '');
    if (!pwd) throw new Error('Senha é obrigatória');
    if (pwd.length < 8) throw new Error('Senha deve ter pelo menos 8 caracteres');

    const exists = (tenant.users || []).some((u) => String(u.email || '').toLowerCase() === normalizedEmail);
    if (exists) throw new Error('Já existe um usuário com esse email nesta empresa');

    tenant.users = [
      {
        id: `u_${uid()}`,
        email: normalizedEmail,
        full_name: name,
        type: MOCK_PROFILES.USER_CLIENTE,
        // mock only: do not use in production
        password: pwd,
        createdAt: new Date().toISOString(),
        allowedSectionsForUser: ['dashboard'],
      },
      ...(tenant.users || []),
    ];
    return state;
  });

export const setSession = ({ profile, tenantId }) =>
  withState((state) => {
    state.session.profile = profile || state.session.profile;
    state.session.tenantId = tenantId || state.session.tenantId;
    return state;
  });

export const setSessionEmail = ({ email }) =>
  withState((state) => {
    state.session.email = (email || '').trim();
    return state;
  });
